import { Globe } from "lucide-react";
import type { ComponentType } from "react";

import { FacebookIcon } from "~/components/icons/facebook-icon";
import { InstagramIcon } from "~/components/icons/instagram-icon";
import { LinkedInIcon } from "~/components/icons/linkedin-icon";
import { YouTubeIcon } from "~/components/icons/youtube-icon";
import { SafeLink } from "~/components/ui/safe-link";
import { normalizeProfileSocials } from "~/lib/shared/social-links";

type Social = {
  href: string;
  label: string;
  Icon: ComponentType<{ className?: string }>;
};

type Props = {
  firstName: string;
  profile: {
    website: string | null;
    linkedinProfile: string | null;
    instagramProfile: string | null;
    facebookProfile: string | null;
    youtubeChannel: string | null;
  };
};

/**
 * Discreet "find me elsewhere" row rendered between the also-section and the
 * closing CTA. Intentionally low visual weight so it doesn't compete with the
 * primary call-to-action — these links leak attention off-platform.
 */
export function CoachSocialsRow({ firstName, profile }: Props) {
  const normalized = normalizeProfileSocials(profile);
  const candidates: Array<Social | "" | null | undefined> = [
    normalized.website && {
      href: normalized.website,
      label: "Website",
      Icon: Globe,
    },
    normalized.linkedinProfile && {
      href: normalized.linkedinProfile,
      label: "LinkedIn",
      Icon: LinkedInIcon,
    },
    normalized.instagramProfile && {
      href: normalized.instagramProfile,
      label: "Instagram",
      Icon: InstagramIcon,
    },
    normalized.facebookProfile && {
      href: normalized.facebookProfile,
      label: "Facebook",
      Icon: FacebookIcon,
    },
    normalized.youtubeChannel && {
      href: normalized.youtubeChannel,
      label: "YouTube",
      Icon: YouTubeIcon,
    },
  ];
  const socials = candidates.filter((s): s is Social => Boolean(s));

  if (socials.length === 0) return null;

  return (
    <div className="border-t border-gray-200 bg-gray-50">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-6 xl:px-0">
        <span className="text-xs font-medium tracking-[0.16em] text-gray-500 uppercase">
          Find {firstName} elsewhere
        </span>
        <div className="flex items-center gap-1">
          {socials.map(({ href, label, Icon }) => (
            <SafeLink
              key={label}
              ugc
              href={href}
              aria-label={`${label} (opens in new tab)`}
              className="flex size-8 items-center justify-center rounded-full text-gray-600 transition-colors hover:bg-white hover:text-gray-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600"
            >
              <Icon className="size-4" />
            </SafeLink>
          ))}
        </div>
      </div>
    </div>
  );
}
